import { useEffect, useImperativeHandle, useState } from "react";
import type { Ref } from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { MonthPicker } from "./MonthPicker";
import {
  type MonthPickerDataProps,
  monthPickerDataSchema,
} from "./MonthPicker.schema";
import { MonthPickerContent } from "./MonthPickerContent";
import { MonthPickerTrigger } from "./MonthPickerTrigger";
import { MonthPickerValue } from "./MonthPickerValue";
import { MONTH_PICKER_TAG_NAME } from "./constants";

export interface MonthPickerHandle {
  value: (value: string | null) => void;
  disabled: (value?: boolean) => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [MONTH_PICKER_TAG_NAME]: {
      props: MonthPickerDataProps;
      handle: MonthPickerHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-month-picker": HTMLElement & MonthPickerHandle;
  }
}

export function RWCMonthPicker({
  ref,
  value: initialValue,
  disabled: initialDisabled,
  placeholder,
  locale,
  min,
  max,
  arrow,
  size,
  layout,
}: MonthPickerDataProps & { ref?: Ref<MonthPickerHandle> }) {
  const host = useIslandHost();

  const [value, setValue] = useState<string | null>(initialValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useImperativeHandle(
    ref,
    () => ({
      value: setValue,
      disabled: (nextDisabled = true) => setDisabled(nextDisabled),
    }),
    [],
  );

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  return (
    <MonthPicker
      locale={locale}
      value={value}
      onValueChange={setValue}
      disabled={disabled}
      min={min}
      max={max}
      arrow={arrow}
      size={size}
      layout={layout}
    >
      <MonthPickerTrigger>
        <MonthPickerValue placeholder={placeholder} />
      </MonthPickerTrigger>
      <MonthPickerContent />
    </MonthPicker>
  );
}

componentManager.register(
  rwc(MONTH_PICKER_TAG_NAME, {
    component: RWCMonthPicker,
    dataSchema: monthPickerDataSchema,
    formAssociated: true,
  }),
);
